import { ReactElement, useState } from "react"
import {
  IconDefinition,
  faSquareCaretDown,
  faEye,
  faPenToSquare,
  faTrash,
  faRotateLeft,
  faBoxArchive,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { ContextMenu, ContextMenuItem, Loading, Pagination, Search, Button, Checkbox } from "@wfp/ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { checkAccess, formatDate } from "utils/helpers"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import HeaderCTA from "components/Shared/HeaderFilters/HeaderCTA"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { GET_ITEM_LIMIT, HH_MODULE } from "constants/constants"
import { BaseXlsForm as BaseXlsFormType } from "interfaces/dataInterfaces"
import { useNavigate } from "react-router-dom"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import {
  useArchiveXlsHHMutation,
  useDeleteXlsFormSchemaMutation,
  useGetBaseXlsFormQuery,
} from "services/household/xlsFormSchemas"
import useCommon from "hooks/useCommon"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

const permissionsProps = {
  moduleId: HH_MODULE,
  subModule: "baseXLSForms",
}

interface IContextItem {
  onAction: () => void
  feature: string
  label: string
  icon: IconDefinition
  color: string
}

const BaseXlsForm = (): ReactElement => {
  const navigate = useNavigate()
  const user = useAppSelector(selectUser)
  const { showSnackBar } = useCommon()

  const [page, setPage] = useState<number>(1)
  const [xlsFormsIsArchived, setxlsFormsIsArchived] = useState<boolean>(false)
  const [pageSize, setPageSize] = useState<number>(GET_ITEM_LIMIT)
  const [query, setQuery] = useState<string>("")
  const [searchValue, setSearchValue] = useState<string>("")
  const [actionItem, setActionItem] = useState<{ action: "delete"; data: BaseXlsFormType } | null>(null)

  const { data, isFetching } = useGetBaseXlsFormQuery({
    search: query,
    skip: page === 1 ? 0 : (page - 1) * pageSize,
    take: pageSize,
    isArchived: xlsFormsIsArchived,
  })
  const [deleteSchema, { isLoading: isDeleting }] = useDeleteXlsFormSchemaMutation()
  const [archive] = useArchiveXlsHHMutation()

  const handleSearch = (): void => {
    setPage(1)
    setQuery(searchValue)
  }

  const handleArchive = async (xlsFormId: number, archived: boolean): Promise<void> => {
    try {
      await archive({ xlsFormId, setArchived: !archived }).unwrap()

      showSnackBar(`XLSForm ${archived ? "restored" : "archived"} successfully`, "success")
    } catch (error) {
      showSnackBar(`Failed to ${archived ? "restore" : "archive"} the XLSForm`, "error")
    }
  }

  const getActionsMenu = (row: BaseXlsFormType) => [
    {
      label: "Details",
      icon: faEye,
      color: "var(--interactive-01)",
      feature: "details",
      onAction: (rowData: BaseXlsFormType) => navigate(`${rowData.xlsFormId}#details`),
    },
    {
      label: "Edit",
      icon: faPenToSquare,
      color: "var(--support-02)",
      feature: "edit",
      onAction: (rowData: BaseXlsFormType) => navigate(`${rowData.xlsFormId}?mode=edit#details`),
    },
    {
      label: row.xlsFormsIsArchived ? "Restore" : "Archive",
      icon: row.xlsFormsIsArchived ? faRotateLeft : faBoxArchive,
      color: row.xlsFormsIsArchived ? "var(--interactive-01)" : "var(--text-02)",
      feature: "edit",
      onAction: (rowData: BaseXlsFormType) => handleArchive(rowData.xlsFormId, rowData.xlsFormsIsArchived),
    },
    {
      label: "Delete",
      icon: faTrash,
      color: "var(--support-01)",
      feature: "delete",
      onAction: (rowData: BaseXlsFormType) => setActionItem({ action: "delete", data: rowData }),
    },
  ]

  const RenderContextItem = ({ onAction, feature, label, icon, color }: IContextItem) => {
    return (
      <PermissionWrapper {...permissionsProps} feature={feature}>
        <ContextMenuItem className="hover:bg-slate-200 p-1.5 px-2 text-left w-28" onClick={onAction}>
          <div>
            <FontAwesomeIcon icon={icon} color={color} className="mr-1" />
            <span style={{ color }}>{label}</span>
          </div>
        </ContextMenuItem>
      </PermissionWrapper>
    )
  }

  const columns: Array<ColumnShape<BaseXlsFormType>> = [
    {
      key: "",
      dataKey: "",
      title: "",
      width: 20,
      minWidth: 20,
      className: "!p-1",
      hidden: data?.items.every(item => item.xlsFormIsValid),
      cellRenderer: ({ rowData }) => (
        <div>
          {!rowData.xlsFormIsValid && (
            <FontAwesomeIcon icon={faCircleExclamation} color="var(--support-01)" title="Invalid XLSForm" />
          )}
        </div>
      ),
    },
    {
      key: "xlsFormId",
      dataKey: "xlsFormId",
      title: "ID",
      width: 80,
    },

    {
      key: "xlsFormName",
      dataKey: "xlsFormName",
      title: "Name",
      width: 600,
      flexGrow: 1,
      resizable: true,
    },

    {
      key: "xlsFormCreateDate",
      title: "Created Date",
      width: 200,
      cellRenderer: ({ rowData }) => formatDate(rowData.xlsFormCreateDate),
    },
    {
      key: "userName",
      dataKey: "userName",
      title: "Created By",
      width: 300,
      minWidth: 200,
      resizable: true,
    },
    {
      key: "action",
      title: "Action",
      width: 80,
      align: Column.Alignment.CENTER,
      hidden: !checkAccess(user?.roles, [permissionsProps.moduleId], permissionsProps.subModule, "all"),
      cellRenderer: ({ rowData }) => (
        <div
          style={{
            minWidth: "2rem",
            minHeight: "2rem",
            borderRadius: "50%",
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={getActionsMenu(rowData).map(item => (
              <RenderContextItem
                key={item.label}
                label={item.label}
                feature={item.feature}
                icon={item.icon}
                color={item.color}
                onAction={() => item.onAction(rowData)}
              />
            ))}
          >
            <div style={{ padding: "0.7rem 1rem" }}>
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  const onDelete = async (): Promise<void> => {
    try {
      await deleteSchema(actionItem?.data.xlsFormId as number).unwrap()
      setActionItem(null)
      showSnackBar("XLSForm deleted successfully", "success")
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar((error?.data as { message: string })?.message ?? "Failed to delete the XLSForm", "error")
    }
  }

  return (
    <>
      {(isFetching || isDeleting) && <Loading small />}
      <HeaderFilters
        left={
          <>
            <Search
              id="search"
              onChange={e => setSearchValue(e.toString())}
              className="mr-1"
              style={{ borderRadius: 5 }}
              name="input-name"
              // @ts-ignore
              closeButtonLabelText="Clear search input"
            />
            <Button onClick={handleSearch}>Search</Button>

            <Checkbox
              wrapperClassName="mx-4 !mb-0 max-w-fit"
              labelText="Show archived"
              onChange={() => {
                setxlsFormsIsArchived(prev => !prev)
                setPage(1)
              }}
              checked={xlsFormsIsArchived}
              value="xlsFormsIsArchived"
              id="xlsFormsIsArchived"
            />
          </>
        }
        right={
          <PermissionWrapper {...permissionsProps} feature="addNew">
            <HeaderCTA onClick={() => navigate("add")} label="Add new base XLSForm schema" />
          </PermissionWrapper>
        }
      />

      <div style={{ height: "calc(100vh - 225px)", minHeight: "60vh" }} className="mt-1">
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={data?.items}
              width={width}
              height={height}
              rowKey="xlsFormId"
              className="custom-table clickable"
              rowHeight={50}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
              rowEventHandlers={{
                onClick: row => {
                  navigate(`/household/baseXlsFormSchema/${row.rowKey}#details`)
                },
              }}
            />
          )}
        </AutoResizer>
      </div>

      <Pagination
        totalItems={data?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[10, 20, 30]}
        page={page}
        onChange={e => {
          const item = e as unknown as { page: number; pageSize: number }
          setPage(item?.page)
          setPageSize(item?.pageSize)
        }}
      />

      <DeleteConfirmationModal
        lazyLoad
        danger
        open={actionItem?.action === "delete"}
        onRequestClose={() => setActionItem(null)}
        onRequestSubmit={onDelete}
      />
    </>
  )
}

export default BaseXlsForm
